import * as amplitude from "@amplitude/analytics-browser";
import { Identify } from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

const serverZone =
  String(import.meta.env.PUBLIC_AMPLITUDE_SERVER_ZONE || "US").toUpperCase() ===
  "EU"
    ? "EU"
    : "US";

const replayPlugin = sessionReplayPlugin({
  sampleRate: 1,
  // Ensures Session Start/End events are captured with the replay ID, which
  // guarantees at least one event per session carries the replay property.
  forceSessionTracking: true,
  // Adds a debug event property used to verify instrumentation. Keep on for
  // local/staging verification, off in production.
  debugMode: import.meta.env.PUBLIC_AMPLITUDE_DEBUG_MODE === "true",
  privacyConfig: {
    defaultMaskLevel: "medium",
    maskSelector: [
      '[class*="ticket"]',
      '[class*="message"]',
      '[class*="comment"]',
    ],
    blockSelector: ["[data-amp-block]"],
  },
});

let initStarted = false;
// Whether the session-replay plugin has been installed on the analytics
// client. It starts OUTSIDE the timeline: while logged out the shell only
// shows the login page, so replay recording/uploading would be wasted traffic.
let replayInstalled = false;

export function initAmplitude() {
  if (initStarted) return;
  initStarted = true;

  const apiKey = import.meta.env.PUBLIC_AMPLITUDE_API_KEY;
  if (!apiKey) {
    console.warn("[Amplitude] API key missing - analytics disabled");
    return;
  }

  // Expose the bridge synchronously so Module Federation remotes
  // (sophos/helios) always find it, even while init() is still in flight.
  if (typeof window !== "undefined") {
    window.__amplitudeAnalytics = {
      setAmplitudeUser,
      resetAmplitudeUser,
      trackEvent,
      getDiagnostics,
    };
  }

  // NOTE: the session-replay plugin is intentionally NOT added here. It is
  // installed lazily by startSessionReplay() once the user is authenticated.
  amplitude
    .init(apiKey, undefined, {
      defaultTracking: {
        pageViews: true,
        sessions: true,
        formInteractions: false,
      },
      serverZone,
    })
    .promise.then(() => {
      console.info("[Amplitude] initialized", getDiagnostics());
    })
    .catch((err) => {
      console.error("[Amplitude] init failed", err);
    });
}

/**
 * Install the session-replay plugin so recording starts. Called once the user
 * is authenticated. Uses the public add() API — the plugin's setup() runs and
 * begins recording against the current analytics session.
 *
 * Why lazy-install instead of toggling the SDK on/off: the plugin's setup()
 * fire-and-forgets its recording start (dynamic import of rrweb), so a later
 * shutdown() can't reliably cancel it — recording would resume and flush the
 * login page repeatedly. Not installing it at all avoids that race entirely.
 */
export function startSessionReplay() {
  if (replayInstalled) return;
  replayInstalled = true;
  amplitude.add(replayPlugin).promise.catch((err) => {
    console.error("[Amplitude] session replay init failed", err);
  });
}

/**
 * Uninstall the session-replay plugin so recording/uploading stops. Called on
 * logout. Uses the public remove() API — teardown() shuts the SDK down.
 */
export function stopSessionReplay() {
  if (!replayInstalled) return;
  replayInstalled = false;
  amplitude.remove(replayPlugin.name).promise.catch((err) => {
    console.error("[Amplitude] session replay stop failed", err);
  });
}

function getDiagnostics() {
  return {
    deviceId: amplitude.getDeviceId() ?? null,
    sessionId: amplitude.getSessionId() ?? null,
    serverZone,
  };
}

export function setAmplitudeUser(user = {}) {
  const userId = user.id;
  if (userId == null) return;

  const str = String(userId);
  amplitude.setUserId(str.length >= 5 ? str : `user_${str}`);

  const identify = new Identify();
  if (user.email) identify.set("email", user.email);
  if (user.fullName) identify.set("name", user.fullName);
  if (user.userType) identify.set("userType", user.userType);
  if (user.clientId) identify.set("clientId", user.clientId);
  if (user.branch?.currency) identify.set("currency", user.branch.currency);
  if (user.client?.accountType) {
    identify.set("accountType", user.client.accountType);
  }
  amplitude.identify(identify);
  amplitude.flush();
}

export function resetAmplitudeUser() {
  amplitude.reset();
}

export function trackEvent(eventName, eventProperties = {}) {
  amplitude.track(eventName, eventProperties);
}
